import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ArchivedCourse, getArchivedCourses, restoreCourse } from '../services/tutorCourseService';

export default function TutorArchivedCoursesPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<ArchivedCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoreLoading, setRestoreLoading] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  useEffect(() => {
    getArchivedCourses()
      .then(setCourses)
      .catch(() => setError('Failed to load archived courses.'))
      .finally(() => setLoading(false));
  }, []);

  const handleRestore = async (courseId: number, courseName: string) => {
    setRestoreLoading(courseId);
    try {
      await restoreCourse(courseId);
      setCourses(prev => prev.filter(c => c.id !== courseId));
      setSnackbar(`"${courseName}" has been restored.`);
    } catch {
      setSnackbar('Failed to restore course. Please try again.');
    } finally {
      setRestoreLoading(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tutor/courses')}
          variant="outlined"
        >
          Back to My Courses
        </Button>
        <Typography variant="h5" fontWeight="bold">
          Archived Courses
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {courses.length === 0 ? (
        <Alert severity="info">No archived courses. Courses you archive will appear here.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Board</TableCell>
                <TableCell>Archived</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courses.map((course) => (
                <TableRow key={course.id} hover>
                  <TableCell>{course.name}</TableCell>
                  <TableCell>{course.subjectName || '—'}</TableCell>
                  <TableCell>{course.board || '—'}</TableCell>
                  <TableCell>{new Date(course.deletedAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={restoreLoading === course.id ? <CircularProgress size={14} /> : <RestoreIcon />}
                      disabled={restoreLoading === course.id}
                      onClick={() => handleRestore(course.id, course.name)}
                    >
                      Restore
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Container>
  );
}
